/**
 * Shared digital-book template for golden fixtures.
 *
 * Mirrors the inline template in `layout/render-book.test.ts` so golden tests
 * and unit tests exercise the same blueprint/variant surface: cover, two
 * category variants (synthesize vs plain), divider, spotlight, TOC, info,
 * filler, back cover.
 */
import { DEFAULT_CANVAS, DEFAULT_FLOW_RULES } from "../dsl/template.js";
import { DEFAULT_CONTRAST_RULES } from "../dsl/tokens.js";
const itemCard = {
    id: 'card',
    kind: 'stack',
    direction: 'column',
    gap: '$sm',
    children: [
        {
            id: 'card-photo',
            kind: 'image',
            source: { binding: 'item.image', placeholder: 'initials' },
            fit: 'cover',
            aspect: 1.4,
        },
        {
            id: 'card-name',
            kind: 'text',
            source: { template: '{{item.name}}' },
            typography: 'itemName',
            color: 'onSurface',
            maxLines: 2,
            autoFit: true,
        },
        {
            id: 'card-desc',
            kind: 'text',
            source: { template: '{{item.description}}' },
            typography: 'itemDescription',
            color: 'onSurfaceMuted',
            maxLines: 3,
            visibleIf: { op: 'exists', path: 'item.description' },
        },
        {
            id: 'card-price',
            kind: 'text',
            source: { template: '{{item.price}}', format: 'price' },
            typography: 'price',
            color: 'accent',
        },
    ],
};
function categoryBlueprint(id, variant, synthesize) {
    return {
        id,
        group: 'category',
        label: `Category ${variant}`,
        variant,
        weight: 10,
        capacity: { min: 2, preferred: 6, max: 12 },
        constraints: { minItems: 1 },
        root: {
            id: `${id}-root`,
            kind: 'stack',
            direction: 'column',
            gap: '$lg',
            style: {
                padding: { top: '$xl', right: '$xl', bottom: '$xl', left: '$xl' },
            },
            children: [
                {
                    id: `${id}-title`,
                    kind: 'text',
                    source: { template: '{{category.name}}' },
                    typography: 'display',
                    color: 'onSurface',
                    visibleIf: {
                        op: 'eq',
                        left: { op: 'var', path: 'page.pageInCategory' },
                        right: { op: 'const', value: 0 },
                    },
                },
                {
                    id: `${id}-grid`,
                    kind: 'grid',
                    columns: 2,
                    gap: '$md',
                    synthesize: synthesize
                        ? {
                            minColumns: 2,
                            maxColumns: 3,
                            heroRatio: 0.25,
                            maxHeroSpan: 2,
                            rhythm: 'organic',
                        }
                        : undefined,
                    children: [
                        {
                            id: `${id}-repeat`,
                            kind: 'repeater',
                            source: 'page.items',
                            as: 'item',
                            itemTemplate: itemCard,
                        },
                    ],
                },
            ],
        },
    };
}
export const GOLDEN_TEMPLATE = {
    id: 'tv_golden',
    templateId: 'tpl_golden',
    version: 1,
    status: 'published',
    canvas: DEFAULT_CANVAS,
    brandable: {
        colors: ['accent', 'surface', 'surfaceAlt'],
        fonts: ['display', 'body'],
        allowRadiusScale: true,
        allowTypeScale: true,
    },
    contrastRules: DEFAULT_CONTRAST_RULES,
    assets: [
        { id: 'flourish', url: 'https://cdn.test/flourish.svg', kind: 'image' },
    ],
    flow: { ...DEFAULT_FLOW_RULES, maxSpotlights: 2 },
    tokens: {
        colors: {
            surface: '#FFFDF8',
            surfaceAlt: '#F2ECE1',
            onSurface: '#1A1714',
            onSurfaceMuted: '#6B6257',
            accent: '#A8341F',
            onAccent: '#FFFFFF',
        },
        typography: {
            display: {
                role: 'display',
                size: 48,
                weight: 600,
                lineHeight: 1.1,
                minSize: 30,
                maxSize: 48,
            },
            itemName: {
                role: 'body',
                size: 20,
                weight: 600,
                lineHeight: 1.25,
                minSize: 14,
                maxSize: 20,
            },
            itemDescription: {
                role: 'body',
                size: 14,
                weight: 400,
                lineHeight: 1.45,
            },
            price: { role: 'accent', size: 18, weight: 600, lineHeight: 1.2 },
        },
        spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 40 },
        radii: { none: 0, md: 8, pill: 999 },
        borders: {
            hairline: { width: 1, style: 'solid', color: 'onSurfaceMuted' },
        },
        shadows: {
            soft: {
                offsetX: 0,
                offsetY: 4,
                blur: 12,
                spread: 0,
                color: 'onSurface',
                opacity: 0.12,
            },
        },
        fonts: {
            display: {
                family: 'Playfair Display',
                source: 'google',
                weights: [600],
                fallback: 'serif',
            },
            body: {
                family: 'Inter',
                source: 'google',
                weights: [400, 600],
                fallback: 'sans-serif',
            },
            accent: {
                family: 'Inter',
                source: 'google',
                weights: [600],
                fallback: 'sans-serif',
            },
        },
    },
    blueprints: [
        {
            id: 'bp_cover',
            group: 'cover',
            label: 'Cover',
            variant: 'cover',
            weight: 10,
            constraints: {},
            root: {
                id: 'cover-root',
                kind: 'stack',
                direction: 'column',
                justify: 'center',
                align: 'center',
                style: { background: 'surfaceAlt' },
                layout: { height: 'full' },
                children: [
                    {
                        id: 'cover-title',
                        kind: 'text',
                        source: { template: '{{restaurant.name}}' },
                        typography: 'display',
                        color: 'onSurface',
                    },
                    {
                        id: 'cover-tagline',
                        kind: 'text',
                        source: {
                            template: '{{restaurant.tagline}}',
                            fallback: '{{menu.title}}',
                        },
                        typography: 'itemDescription',
                        color: 'onSurfaceMuted',
                    },
                ],
            },
        },
        categoryBlueprint('bp_cat_a', 'A', true),
        categoryBlueprint('bp_cat_b', 'B', false),
        {
            id: 'bp_divider',
            group: 'divider',
            label: 'Divider',
            variant: 'divider',
            weight: 10,
            constraints: {},
            root: {
                id: 'divider-root',
                kind: 'stack',
                direction: 'column',
                justify: 'center',
                align: 'center',
                layout: { height: 'full' },
                children: [
                    {
                        id: 'divider-title',
                        kind: 'text',
                        source: { template: '{{category.name}}', format: 'uppercase' },
                        typography: 'display',
                        color: 'accent',
                    },
                ],
            },
        },
        {
            id: 'bp_spotlight',
            group: 'spotlight',
            label: 'Spotlight',
            variant: 'spotlight',
            weight: 10,
            constraints: {},
            root: {
                id: 'spotlight-root',
                kind: 'stack',
                direction: 'column',
                layout: { height: 'full' },
                children: [
                    {
                        id: 'spotlight-photo',
                        kind: 'image',
                        source: { binding: 'item.image', placeholder: 'pattern' },
                        fit: 'cover',
                        layout: { grow: 1 },
                    },
                    {
                        id: 'spotlight-name',
                        kind: 'text',
                        source: { template: '{{item.name}}' },
                        typography: 'display',
                        color: 'onSurface',
                    },
                ],
            },
        },
        {
            id: 'bp_toc',
            group: 'toc',
            label: 'Contents',
            variant: 'toc',
            weight: 10,
            constraints: {},
            root: {
                id: 'toc-root',
                kind: 'stack',
                direction: 'column',
                gap: '$md',
                children: [
                    {
                        id: 'toc-repeat',
                        kind: 'repeater',
                        source: 'book.contents',
                        as: 'entry',
                        itemTemplate: {
                            id: 'toc-row',
                            kind: 'text',
                            source: { template: '{{entry.title}} — {{entry.page}}' },
                            typography: 'itemName',
                            color: 'onSurface',
                        },
                    },
                ],
            },
        },
        {
            id: 'bp_info',
            group: 'info',
            label: 'Info',
            variant: 'info',
            weight: 10,
            constraints: {},
            root: {
                id: 'info-root',
                kind: 'text',
                source: {
                    template: '{{restaurant.address}}',
                    fallback: '{{restaurant.name}}',
                },
                typography: 'itemName',
                color: 'onSurface',
            },
        },
        {
            id: 'bp_filler',
            group: 'filler',
            label: 'Filler',
            variant: 'filler',
            weight: 1,
            constraints: {},
            root: {
                id: 'filler-root',
                kind: 'shape',
                shape: 'rect',
                fill: 'surfaceAlt',
            },
        },
        {
            id: 'bp_back',
            group: 'backCover',
            label: 'Back cover',
            variant: 'back',
            weight: 10,
            constraints: {},
            root: {
                id: 'back-root',
                kind: 'text',
                source: { template: '{{menu.footnote}}', fallback: 'Thank you' },
                typography: 'itemDescription',
                color: 'onSurfaceMuted',
            },
        },
    ],
};
/** Fixed clock for every golden render — availability windows stay reproducible. */
export const GOLDEN_NOW = new Date('2026-08-03T19:00:00Z');
//# sourceMappingURL=template.js.map